import React, { useContext, useEffect, useState } from "react";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwButton from "../../shared/OwButton/OwButton";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import OwTable from "../../shared/OwTable/OwTable";
import { OwFnTable } from "../../shared/OwFnTable/OwFnTable";
import IRoute from "../../../models/IRoute";
import IOwTableCols from "../../../models/IOwTableCols";
import { SocketContext } from "../../../contexts/SocketContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IOnGetRoutesDTO from "../../../models/DTO/IOnGetRoutesDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IGetRoutesDTO from "../../../models/DTO/IGetRoutesDTO";
import { RouteSidebar } from "../RouteSidebar/RouteSidebar";
import UiContext from "../../../contexts/UiContext";
import OwBadge from "../../shared/OwBadge/OwBadge";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";

type Props = {};

export const RoutesView = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const getBreadcrumbs = () => {
        return [
            {
                title: "daily_routes",
                url: "/routes/daily",
            },
            {
                title: "routes",
                url: "/routes",
            },
        ];
    };

    const renderActions = (row: IRoute) => {
        return (
            <div className={"d-flex align-items-center"}>
                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => {}}
                    onEditClickFn={() => {
                        openRoutesSidebar(row);
                    }}
                    hideTrash={true}
                    className={"me-2"}
                />
            </div>
        );
    };

    const renderName = (row: IRoute) => {
        return (
            <div className={"d-flex align-items-center"}>
                {row.name}
                {row.is_pickup ? (
                    <div>
                        <OwBadge title={"pickup"} bgColor={"bg-primary"} />
                    </div>
                ) : null}
            </div>
        );
    };

    const renderDefaultDriver = (row: IRoute) => {
        return <div className={"d-flex align-items-center"}>{row.Default_User?.name || "-"}</div>;
    };

    const formatStartTime = (time?: string | null) => {
        if (!time) return "-";
        const [hStr, m] = time.split(":");
        const h = parseInt(hStr, 10);
        if (isNaN(h)) return time;
        const ampm = h >= 12 ? "PM" : "AM";
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return `${h12}:${m ?? "00"} ${ampm}`;
    };

    const renderDefaultStartTime = (row: IRoute) => {
        return (
            <div className={"d-flex align-items-center"}>
                {row.default_start_time ? <i className={"far fa-clock me-2 text-muted"} /> : null}
                {formatStartTime(row.default_start_time)}
            </div>
        );
    };

    const getTableCols = (): IOwTableCols[] => {
        return [
            {
                title: "",
                valueProp: "",
                isActionsCol: true,
                renderFn: renderActions,
                renderFnEmitsRow: true,
                className: "w-10r",
            },
            {
                title: "name",
                valueProp: "name",
                renderFn: renderName,
                renderFnEmitsRow: true,
                className: "w-20r",
            },
            {
                title: "default_start_time",
                valueProp: "default_start_time",
                renderFn: renderDefaultStartTime,
                renderFnEmitsRow: true,
                className: "w-15r",
            },
            {
                title: "default_driver",
                valueProp: "default_driver",
                renderFn: renderDefaultDriver,
                renderFnEmitsRow: true,
            },
        ];
    };

    const [crumbs, setCrumbs] = useState<IBreadcrumbs[]>(getBreadcrumbs());

    const [tableCols, setTableCols] = useState(getTableCols());
    const [isDataFetching, setIsDataFetching] = useState(true);
    const [isInitialData, setIsInitialData] = useState(true);
    const [routes, setRoutes] = useState<IRoute[]>([]);

    const getRoutes = () => {
        const payload: IGetRoutesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.RoutesGetRoutes, payload);

        setIsDataFetching(true);
    };

    const onGetRoutes = (data: IOnGetRoutesDTO) => {
        setRoutes(data.routes);
        setIsDataFetching(false);
        setIsInitialData(false);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.RoutesOnGetRoutes, fn: onGetRoutes }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getRoutes();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const closeSidebar = (refresh?: boolean) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (refresh) {
            getRoutes();
        }
    };

    const openRoutesSidebar = (route?: IRoute) => {
        const sideBarComponent = <RouteSidebar cancelFn={closeSidebar} route={route} />;

        uiContext.setSidebarComponent(sideBarComponent);
        uiContext.toggleSidebar(true);
    };

    return (
        <div className={"Routes"}>
            <Breadcrumbs crumbs={crumbs} />

            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <OwTitle title={"routes"} leftIconName={"fad fa-road"} />
                    <div>
                        <OwButton
                            className={"ms-2 btn-warning"}
                            title={"create"}
                            leftIconClass={"fas fa-plus"}
                            fn={() => {
                                openRoutesSidebar();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div>
                <OwFnTable columns={tableCols} dataSet={routes} dataFetch={isDataFetching} initialData={isInitialData} />
            </div>
        </div>
    );
};
